import { computed } from 'vue';
import {
  useLoadingCardCustomisation,
  useErrorCardCustomisation,
  useEmptyListCustomisation,
  useSelectButtonCustomisation,
  useLoadMoreButtonCustomisation,
  useCancelButtonCustomisation,
} from './useTransactionListCustomisation';
import { useTransactionCardCustomisation } from './useTransactionItemCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';
import { tConversation } from '@/plugins/i18n';

export const useTransactionListStyles = () => {
  // Customisation hooks
  const loadingCard = useLoadingCardCustomisation();
  const errorCard = useErrorCardCustomisation();
  const emptyList = useEmptyListCustomisation();
  const selectButton = useSelectButtonCustomisation();
  const loadMoreButton = useLoadMoreButtonCustomisation();
  const cancelButton = useCancelButtonCustomisation();
  const transactionCard = useTransactionCardCustomisation();

  // Computed styles for loading card
  const loadingCardStyle = computed(() =>
    SUtils.combineStyles(
      SUtils.createBackgroundStyle(loadingCard.backgroundColor),
      SUtils.createBorderStyle(loadingCard.borderColor)
    )
  );
  const loadingTextStyle = computed(() => SUtils.createTextStyle(loadingCard.textColor));

  // Computed styles for error card
  const errorCardStyle = computed(() =>
    SUtils.combineStyles(
      SUtils.createBackgroundStyle(errorCard.backgroundColor),
      SUtils.createBorderStyle(errorCard.borderColor)
    )
  );
  const errorTextStyle = computed(() => SUtils.createTextStyle(errorCard.textColor));
  const errorDetailsStyle = computed(() => SUtils.createTextStyle(errorCard.subTextColor));

  // Computed styles for empty list
  const emptyListStyle = computed(() =>
    SUtils.combineStyles(
      SUtils.createBackgroundStyle(emptyList.backgroundColor),
      SUtils.createBorderStyle(emptyList.borderColor)
    )
  );
  const emptyTextStyle = computed(() => SUtils.createTextStyle(emptyList.textColor));

  // Button styles
  const selectButtonStyles = computed(() => ({
    actionColor: selectButton.buttonBackgroundColor.value,
    textColor: selectButton.buttonTextColor.value,
  }));

  const selectButtonDisabledStyles = computed(() => ({
    actionColor: selectButton.disabledButtonBackgroundColor.value,
    textColor: selectButton.disabledButtonTextColor.value,
  }));

  const loadMoreButtonStyles = computed(() => ({
    actionColor: loadMoreButton.buttonBackgroundColor.value,
    textColor: loadMoreButton.buttonTextColor.value,
  }));

  const cancelButtonStyles = computed(() => ({
    actionColor: cancelButton.buttonBackgroundColor.value,
    textColor: cancelButton.buttonTextColor.value,
  }));

  // Factory functions for button text (accept props as needed)
  const getSelectButtonText = (isSubmitting: boolean, submitButtonText: string) => {
    if (isSubmitting) return tConversation('global.please_wait');
    return selectButton.buttonText.value || submitButtonText;
  };

  const getLoadMoreButtonText = (loading: boolean) => {
    return loading
      ? tConversation('global.please_wait')
      : loadMoreButton.buttonText.value || tConversation('page.issue.transaction.next');
  };

  // Static text values that don't need parameters
  const cancelButtonText = computed(
    () => cancelButton.buttonText.value || tConversation('page.issue.transaction.cancel_request')
  );
  const emptyListText = computed(() => emptyList.textContent.value || tConversation('page.issue.transaction.none'));

  // Factory functions for "not listed" option styles
  const getNotListedContainerStyle = (isSelected: boolean) => {
    return SUtils.combineStyles(
      SUtils.createBackgroundStyle(
        isSelected ? transactionCard.selectedBackgroundColor : transactionCard.backgroundColor
      ),
      SUtils.createBorderStyle(isSelected ? transactionCard.selectedBorderColor : transactionCard.borderColor)
    );
  };

  const getNotListedRadioStyle = (isSelected: boolean) => {
    return SUtils.createBorderStyle(isSelected ? transactionCard.selectedIconColor : transactionCard.iconColor);
  };

  const notListedRadioDotStyle = computed(() => SUtils.createBackgroundStyle(transactionCard.selectedIconColor));

  const notListedTextStyle = computed(() => SUtils.createTextStyle(transactionCard.textColor));

  return {
    // Loading card
    loadingCardStyle,
    loadingTextStyle,

    // Error card
    errorCardStyle,
    errorTextStyle,
    errorDetailsStyle,

    // Empty list
    emptyListStyle,
    emptyTextStyle,

    // Button styles
    selectButtonStyles,
    selectButtonDisabledStyles,
    loadMoreButtonStyles,
    cancelButtonStyles,

    // Factory functions for dynamic text content
    getSelectButtonText,
    getLoadMoreButtonText,

    // Static text content
    cancelButtonText,
    emptyListText,

    // Factory functions for "not listed" option styles
    getNotListedContainerStyle,
    getNotListedRadioStyle,
    notListedRadioDotStyle,
    notListedTextStyle,
  };
};
